import {
  IEntryRecordLabels,
  IGenericRequestParametersTranslator,
  IModificationRecordLabels,
  INamedEntityLabels,
  IUniqueEntityLabels,
  PropertyTranslator,
} from "./GenericTranslator";
import {
  EntityConstants,
  EntryRecordOrderBy,
  GenericEntityOrderBy,
  IEnteredOn,
  IEntityMinimalModel,
  IEntryRecord,
  IGenericRequestParameters,
  IModelWithIntId,
  IModelWithStringId,
  IModificationRecord,
  IModifiedOn,
  INamedEntity,
  IUniqueEntity,
  ModificationRecordOrderBy,
  NamedEntityOrderBy,
} from "./GenericTypes";

// Credentials
export interface ZontalCredentialsReadModel extends IModelWithIntId, IEntryRecord, IModificationRecord, IUniqueEntity {
  id: number;
  username: string;
  host: string;
  clientId: string;
}

export interface ZontalCredentialsWriteModel extends ZontalCredentialsReadModel {
  password: string;
  clientSecret: string;
}

export interface ZontalCredentialsFilters {}

export const zontalCredentialsConstants: EntityConstants<ZontalCredentialsWriteModel, ZontalCredentialsFilters> = {
  resource: "zontal/credentials",
  frontendIndexRoute: "zontal_credentials",
  entitySingular: "ZONTAL credential",
  entityPlural: "ZONTAL credentials",
  icon: "blocks",
};

export const ZontalCredentialsFieldLabels: PropertyTranslator<ZontalCredentialsWriteModel> = {
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...IUniqueEntityLabels,
  id: "ID",
  username: "Username",
  password: "Password",
  host: "Host",
  clientId: "Client ID",
  clientSecret: "Client Secret",
} as const;

// Information package profile

export const zontalIPPConstants: EntityConstants<
  ZontalInformationPackageProfile,
  ZontalInformationPackageProfileFilters
> = {
  resource: "zontal/information_package",
  frontendIndexRoute: "zontal_information_package",
  entitySingular: "ZONTAL Information Package",
  entityPlural: "ZONTAL Information Packages",
  icon: "activity",
};

interface ZontalPersonModel extends IEntityMinimalModel<string> {
  givenName?: string;
  familyName?: string;
  mbox?: any[];
}
export interface ZontalInformationPackageProfile
  extends IModelWithStringId,
    INamedEntity,
    IModifiedOn,
    IEnteredOn,
    IUniqueEntity {
  enteredBy: ZontalPersonModel;
  lastModifiedBy: ZontalPersonModel;
  uid: string;
  owners: ZontalPersonModel[];
  version: number;
  adfIdentifier: string; // Guid in C#
  adfVersion: string[];
  digitalObjectsContentLength: number;
  digitalObjectsCount: number;
  contentIdentifier: string;
  informationPackageProfile: IEntityMinimalModel<string>;
  lifecycleStatus: IEntityMinimalModel<string>;
  transitionStatus: IEntityMinimalModel<string>;
  datahubLoaded: boolean;
  submittedOn: string; // DateTime in C#
  submittedBy: ZontalPersonModel; // UserInfoRead, type unknown in TS, replace with correct type if available
  motivatedBy: string;
  expired: boolean;
  digestValue: string;
}

export interface ZontalInformationPackageProfileSuggestions
  extends IEntityMinimalModel<ZontalInformationPackageProfile["id"]> {}

export const ZontalInformationPackageSortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
] as const;
export type ZontalInformationPackageSortingOptions = (typeof ZontalInformationPackageSortingOptionsConsts)[number];

export interface ZontalInformationPackageProfileFilters
  extends IGenericRequestParameters<ZontalInformationPackageProfile, ZontalInformationPackageSortingOptions> {
  names?: string[];
  enteredFrom?: string | null;
  enteredTo?: string | null;
  enteredBy?: string[] | null;
  modifiedFrom?: string | null;
  modifiedTo?: string | null;
  modifiedBy?: string[] | null;
  excludeIds?: string[];
  searchTerm?: string;
  ids?: string[];
  searchAfter?: string;
  informationPackageProfiles?: string[] | null;
}

export const ZontalInformationPackageProfileFiltersTranslator: PropertyTranslator<ZontalInformationPackageProfileFilters> =
  {
    ...IGenericRequestParametersTranslator,
    names: "Names",
    enteredFrom: "Entered from",
    enteredTo: "Entered to",
    modifiedFrom: "Modified from",
    modifiedTo: "Modified to",
    enteredBy: "Entered by",
    modifiedBy: "Modified by",
    excludeIds: "Exclude IDs",
    searchTerm: "Search term",
    informationPackageProfiles: "Information Package Profiles",
    ids: "IDs",
    searchAfter: "Search after",
  } as const;

export const ZontalInformationPackageProfileFieldLabels: PropertyTranslator<ZontalInformationPackageProfile> = {
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...IUniqueEntityLabels,
  ...INamedEntityLabels,
  id: "ID",
  uid: "UID",
  version: "Version",
  adfIdentifier: "ADF Identifier",
  adfVersion: "ADF Version",
  digitalObjectsContentLength: "Digital Objects Content Length",
  digitalObjectsCount: "Digital Objects Count",
  contentIdentifier: "Content Identifier",
  informationPackageProfile: "Information Package Profile",
  lifecycleStatus: "Lifecycle Status",
  transitionStatus: "Transition Status",
  datahubLoaded: "Datahub Loaded",
  submittedOn: "Submitted On",
  submittedBy: "Submitted By",
  motivatedBy: "Motivated By",
  expired: "Expired",
  digestValue: "Digest Value",
  lastModifiedBy: "Last Modified By",
  enteredBy: "Entered By",
  owners: "Owners",
} as const;
